import {
  Button,
  Icon,
  Label,
  ProgressBar,
  Switch,
  Tooltip,
} from '@blueprintjs/core';
import AppContentView from 'Components/Generic/AppContentView';
import BOSDateRangeInput from 'Components/Generic/BOSDateInput/BOSDateRangeInput';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import DetailPanel from 'Components/Generic/DetailPanel';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { useGlobalState } from 'Data/context/GlobalState';
import {
  getUserManagingOrgIds,
  getUserOrgIds,
  useOrgContext,
} from 'Data/context/Organizations';
import DataManager from 'Lib/DataManager';
import { useAutoRefresh } from 'Lib/hooks/useAutoRefresh';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import { useEffect, useState } from 'react';
import { canUserViewAllCores } from 'Util';
import { getFiscalYearsFromRange } from 'Util/dateUtil';
import ChargeAccountReportTable from './ChargeAccountReportTable';
import { CURRENT_FISCAL_YEAR_DR, DATE_FIELDS } from './Enum';
import styles from './Reports.module.css';

let DM = new DataManager();

const Reports = () => {
  const { fullOrgList } = useOrgContext();
  const [globalState, updateGlobalState] = useGlobalState();
  const [isLoading, setIsLoading] = useState(false);
  const [reportOptions, setReportOptions, reportOptionsRef] = useStateWithRef(
    globalState.reportOptions || {
      dateRange: CURRENT_FISCAL_YEAR_DR.dateRange,
      dateField: 'date_cleared',
      includeOpen: true,
      ...(!canUserViewAllCores() && {
        organizations: fullOrgList.filter((org) =>
          getUserManagingOrgIds().includes(org.id),
        ),
      }),
    },
  );
  const [reportData, setReportData, reportDataRef] = useStateWithRef(
    globalState.reportData,
  );
  const [errorMessage, setErrorMessage] = useState();
  const reloadReportData = () => {
    retrieveReportData();
    setIsLoading(true);
    setErrorMessage(undefined);
  };

  const {
    initiateAutoRefresh,
    startRefreshInterval,
    resetAutoRefresh,
    isInitialLoad,
  } = useAutoRefresh(reloadReportData);

  useEffect(() => {
    reportData && initiateAutoRefresh();
  }, []);

  useEffect(() => {
    setIsLoading(false);
    reportData && startRefreshInterval();
  }, [reportData]);

  useEffect(() => {
    DM.add(
      ChargeAllocationData.registerListener(
        'CA_REPORT_LIST_LOADED',
        onDataLoaded,
      ),
      ChargeAllocationData,
    );
    DM.add(
      ChargeAllocationData.registerListener('LIST_LOAD_ERROR', onDataLoadError),
      ChargeAllocationData,
    );

    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    return () => {
      updateGlobalState('reportOptions', reportOptionsRef.current);
      updateGlobalState('reportData', reportDataRef.current);
    };
  }, []);

  const runReportClicked = () => {
    initiateAutoRefresh();
  };

  const retrieveReportData = () => {
    let options = {};
    if (reportOptions?.organizations?.length > 0) {
      options.organizations = reportOptions.organizations.map((org) => org.id);
    } else if (!canUserViewAllCores()) {
      options.organizations = getUserManagingOrgIds();
    }
    if (reportOptions?.dateRange?.length > 0) {
      options.date_range = reportOptions.dateRange;
      options.fiscal_years = getFiscalYearsFromRange(reportOptions.dateRange);
    }
    if (reportOptions?.dateField) {
      options.date_field = reportOptions.dateField;
    }
    if (
      reportOptions?.dateField === 'date_cleared' &&
      reportOptions.includeOpen
    ) {
      options.include_open = reportOptions.includeOpen;
    }
    ChargeAllocationData.getReportList(options);
  };

  const onDataLoaded = (dataPayload) => {
    setReportData(dataPayload);
  };
  const onDataLoadError = (error) => {
    resetAutoRefresh();
    setIsLoading(false);
    setErrorMessage(error?.message || 'Error');
  };
  const updateOptions = (optionKey, newValue) => {
    if (reportData) {
      resetAutoRefresh();
      setReportData({
        count: reportData.count,
      });
    }
    setReportOptions({ ...reportOptions, [optionKey]: newValue });
  };

  let runReportButton = (
    <Button
      className={
        isLoading && isInitialLoad
          ? styles.run_report_button_disabled
          : styles.run_report_button
      }
      onClick={runReportClicked}
      icon={'rocket'}>
      Run Report
    </Button>
  );

  let excludedOrgIds = canUserViewAllCores()
    ? []
    : getUserOrgIds().filter((id) => !getUserManagingOrgIds().includes(id));

  let runReportFooter = (
    <ButtonFooter marginTop={2}>{runReportButton}</ButtonFooter>
  );

  let reportOptionsPanel = (
    <DetailPanel
      collapsible
      marginBottom={0}
      disabled={isLoading && isInitialLoad}
      title={'Options'}
      hideTitleIfExpanded
      footer={runReportFooter}>
      <div className={styles.options_flex_box}>
        <div>
          <Label className={styles.options_label}>Date Field</Label>
          <BOSSelect
            inputClassName={styles.report_input}
            listData={Object.entries(DATE_FIELDS).map(([key, value]) => {
              return {
                id: key,
                name: value,
              };
            })}
            onItemSelect={(item) => updateOptions('dateField', item.id)}
            defaultSelectedId={reportOptions.dateField}
          />
        </div>
        {reportOptions?.dateField === 'date_cleared' && (
          <div style={{ flexGrow: 0 }}>
            <Tooltip content={'Include open and pending charges'}>
              <Label className={styles.options_label}>Open/Pending</Label>
            </Tooltip>
            <div style={{ paddingLeft: 4, paddingTop: 8 }}>
              <Switch
                className={`${styles.options_switch} ${
                  reportOptions?.includeOpen
                    ? styles.options_switch_checked
                    : styles.options_switch_unchecked
                }`}
                checked={reportOptions?.includeOpen}
                innerLabelChecked={
                  <Icon
                    icon={'tick'}
                    size={16}
                    style={{ padding: 4, paddingRight: 6 }}
                  />
                }
                onChange={() =>
                  updateOptions('includeOpen', !reportOptions.includeOpen)
                }
              />
            </div>
          </div>
        )}
        <div style={{ flexGrow: 0 }}>
          <Label className={styles.options_label}>Date Range</Label>
          <BOSDateRangeInput
            defaultValue={CURRENT_FISCAL_YEAR_DR}
            defaultRange={globalState.reportOptions?.dateRange}
            onChange={(dateRange) => updateOptions('dateRange', dateRange)}
          />
        </div>
        {(canUserViewAllCores() || getUserManagingOrgIds().length > 1) && (
          <div>
            <Tooltip
              content={
                'Select 1 or more Core Facilities.  If none are selected, the report will run for all.'
              }>
              <Label className={styles.options_label}>Core Facilities</Label>
            </Tooltip>
            <OrganizationMultiselect
              hideLabel
              showClearButton
              maxWidth={340}
              defaultSelectedItems={globalState.reportOptions?.organizations}
              onSelectedOrganizationsUpdate={(orgList) =>
                updateOptions('organizations', orgList)
              }
              excludedIds={excludedOrgIds}
              autofocus={false}
            />
          </div>
        )}
      </div>
    </DetailPanel>
  );

  let errorComponent = errorMessage && <div>{`${errorMessage}`}</div>;
  let reportTablePanel = (
    <>
      <div
        className={`${
          isLoading ? styles.report_table_box_loading : styles.report_table_box
        } bp5-card`}>
        {isLoading && isInitialLoad ? (
          <ProgressBar />
        ) : errorMessage ? (
          errorComponent
        ) : !reportData?.records ? (
          reportData?.count ? (
            <NoContentBox
              label={
                "Report criteria have changed... Click 'Run Report' to re-run the report"
              }
            />
          ) : (
            <NoContentBox
              label={"Select criteria above and click 'Run Report'"}
            />
          )
        ) : reportData?.records?.length > 0 ? (
          <ChargeAccountReportTable
            reportData={reportData}
            reportOptions={reportOptions}
          />
        ) : (
          reportData?.records?.length === 0 && (
            <NoContentBox label={'No results for given criteria'} />
          )
        )}
      </div>
    </>
  );

  return (
    <AppContentView title={'Reports'} noPadding hideBreadcrumbs>
      <div className={styles.content_box}>
        {reportOptionsPanel}
        {reportTablePanel}
      </div>
    </AppContentView>
  );
};

export default Reports;
